import { useState, useCallback, useRef } from 'react';
import { Typography, Table, Input, Tag, Button, Avatar } from 'antd';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getTeachers } from '@/services/teachers';
import { STATUS_COLORS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Teacher } from '@/types';

dayjs.extend(relativeTime);

export default function TeachersPage() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const page = cursorHistory.length + 1;
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({
    queryKey: ['teachers', search, cursor],
    queryFn: () => getTeachers({ search: search || undefined, cursor, limit: 20 }),
  });

  const teachers: Teacher[] = data?.data?.data ?? [];
  const meta = data?.data?.meta;
  const total = (meta as Record<string, unknown>)?.total as number ?? 0;

  const resetPagination = useCallback(() => { setCursor(undefined); setCursorHistory([]); }, []);
  const handleNext = useCallback(() => { setCursorHistory((h) => [...h, cursor]); setCursor(meta?.cursor); }, [cursor, meta?.cursor]);
  const handlePrev = useCallback(() => { const h = [...cursorHistory]; const prev = h.pop(); setCursorHistory(h); setCursor(prev); }, [cursorHistory]);
  const handleSearch = useCallback((value: string) => { setSearch(value); resetPagination(); }, [resetPagination]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(value); resetPagination(); }, 300);
  }, [resetPagination]);

  const columns = [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (name: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{name}</span>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string) => <span style={{ color: '#64748b' }}>{v}</span> },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v: string | undefined) => <span style={{ color: '#64748b' }}>{v || '—'}</span> },
    {
      title: 'Status', key: 'status',
      render: (_: unknown, r: Teacher) => {
        const effectiveStatus =
          (r.teacherStatus === 'REJECTED' || r.teacherStatus === 'PENDING_APPROVAL' || r.teacherStatus === 'SUSPENDED')
            ? r.teacherStatus
            : r.status;
        const label = effectiveStatus === 'PENDING_APPROVAL' ? 'PENDING' : effectiveStatus;
        const color = STATUS_COLORS[effectiveStatus] ?? STATUS_COLORS.INACTIVE;
        return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#fff', background: color }}>{label}</span>;
      },
    },
    { title: 'Students', key: 'students', render: (_: unknown, r: Teacher) => <Tag style={{ borderRadius: 12 }}>{r._count?.teacherConnections ?? 0}</Tag> },
    { title: 'Decks', key: 'decks', render: (_: unknown, r: Teacher) => <Tag style={{ borderRadius: 12 }}>{r._count?.decks ?? 0}</Tag> },
    {
      title: 'Last Active', dataIndex: 'lastActiveAt', key: 'lastActiveAt',
      render: (v: string | null) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v ? dayjs(v).fromNow() : 'Never'}</span>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Teachers</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Manage teacher accounts and their students</Typography.Text>
      </div>

      <Input
        placeholder="Search teachers…"
        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
        allowClear
        onChange={(e) => {
          if (!e.target.value) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            handleSearch('');
          } else {
            handleSearchChange(e);
          }
        }}
        style={{
          flex: "1 1 180px",
          maxWidth: 260,
          height: 32,
          marginBottom: 16,
        }}
      />

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : teachers.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No teachers found</div>
          ) : teachers.map((r) => {
            const effectiveStatus =
              (r.teacherStatus === 'REJECTED' || r.teacherStatus === 'PENDING_APPROVAL' || r.teacherStatus === 'SUSPENDED')
                ? r.teacherStatus
                : r.status;
            const statusLabel = effectiveStatus === 'PENDING_APPROVAL' ? 'PENDING' : effectiveStatus;
            const statusColor = STATUS_COLORS[effectiveStatus] ?? STATUS_COLORS.INACTIVE;
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/admin/users/${r.id}`)}
                style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Avatar size={38} style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', fontWeight: 700, flexShrink: 0, fontSize: 15 }}>
                    {r.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{r.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                    {r.phone && <div style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.phone}</div>}
                  </div>
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#fff', background: statusColor, flexShrink: 0 }}>
                    {statusLabel}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 16, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    <span style={{ color: '#94a3b8' }}>Students </span>
                    <Tag style={{ borderRadius: 12, margin: 0 }}>{r._count?.teacherConnections ?? 0}</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    <span style={{ color: '#94a3b8' }}>Decks </span>
                    <Tag style={{ borderRadius: 12, margin: 0 }}>{r._count?.decks ?? 0}</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                    {r.lastActiveAt ? dayjs(r.lastActiveAt).fromNow() : 'Never'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={teachers}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
          onRow={(record) => ({ onClick: () => navigate(`/admin/users/${record.id}`), style: { cursor: 'pointer' } })}
        />
      )}

      <div className="cw-pagination">
        <span className="cw-page-info">Page {page}{total > 0 && <> &middot; {total.toLocaleString()} teacher{total !== 1 ? 's' : ''}</>}</span>
        <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={cursorHistory.length === 0}>Previous</Button>
        <Button icon={<RightOutlined />} onClick={handleNext} disabled={!meta?.hasMore}>Next</Button>
      </div>
    </div>
  );
}
